/**
 * 작성자 : 한영광
 * 날짜 : 2023.05.10
 * 기능 : TOAST UI 라이브러리로 테이블 생성하는 컴포넌트
 */
/**
 * 수정자 : 한영광
 * 날짜 : 2023.09.05
 * 수정내용 : 정적 컴포넌트로 TUI-GRID를 사용하면서 자동 리로드 되는 부분 개선을 위해 동적인 컴포넌트로 수정함
 * 참고자료 : https://github.com/nhn/tui.grid/blob/master/packages/toast-ui.grid/docs/ko/README.md
 */
import { TuiGridStyle } from '../../assets/styles/TuiGridStyle';
import Grid from '@toast-ui/react-grid';
import { useRef, useEffect, useState, Dispatch, SetStateAction, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { asideFoldState } from '../../atoms';
import { useRecoilValue } from 'recoil';
import { maskSecretValue } from '../../utils/masking';
import { normalizeMaskingValue } from '../../utils/normalizePhoneNumber';

interface Props {
  data: any[];
  filterTableState?: any[];
  setFilterTableState?: Dispatch<SetStateAction<any>>;
  columns: any[];
  onClickRow?: Function;
  clickDisable?: boolean;
  perPage?: number; // 페이지 당 보여줄 로우 갯수
  height?: any;
  usePagenation?: boolean; // 페이징 사용유무
  rowKey?: string; // 선택된 로우 index (선택된 로우를 리렌더링 시에도 유지하기위해 사용. onClickRow 함수를 통해 상위컴포넌트로 전달함)
  excelBtn?: boolean;
  setExcelBtn?: Dispatch<SetStateAction<any>>;
  onClickCheckBox?: Function;
  visibleTotalCount?: boolean;
  scrollX?: boolean;
  visiblePages?: number; // pagenation 사용 시 보여줄 페이지 갯수
  rowHeight?: any;
  frozenCount?: number; // 고정시킬 컬럼 갯수(좌측부터 시작)
  dashboard?: boolean; // 대시보드에서 사용하는건지 구분
  eListYn?: string; // 근로자 명 일부 가릴지 여부 ('Y' or 'N')
  eExcelYn?: string; // 엑셀 내보내기 시 근로자 명 일부 가릴지 여부 ('Y' or 'N')
  rowClickCheckBox?: boolean; // 로우 클릭 시 체크박스 체크 처리 할지 여부
  checkedColumn?: { columnName: string | null; checked: boolean }; // tuiGrid 외부의 컬럼 선택값
  modal?: boolean; // 모달창인지
  summary?: any; // 테이블 고정 로우
  fileName?: string;
  setRowKey?: Dispatch<SetStateAction<any>>;
  customCheck?: boolean; // tuigrid 로우 내부 체크박스 여부
  draggable?: boolean; // 드래그가능 여부
  isDisable?: boolean;
  key?: string;
  isUnChkAll?: boolean; // 이벤트 처리후 모든 체크박스 비활성화 시키는 여부
  comparisonChkKey?: string; // 비교 리스트 키 네임
  comparisonChkList?: any[]; // 테이블 리스트와 비교하여 있는 대상일 경우 체크
  pageNum?: number;
  gridId?: string;
  idxKey?: string;
  /**
   * 작성자 : 한영광
   * 작성날짜 : 2023-09-07
   * summary 타입
    {
      height?: number;
      position?: SummaryPosition;
      defaultContent?: string | SummaryColumnContentMapOnlyFn;
      columnContent?: {
        [propName: string]: string | SummaryColumnContentMapOnlyFn;
      };
    }
   */
}

const TuiGrid = (props: Props) => {
  const gridRef = useRef<Grid | null>(null);
  const grid = gridRef.current?.getInstance();

  const {
    key,
    data,
    filterTableState,
    setFilterTableState,
    columns,
    perPage = 15,
    onClickRow,
    clickDisable,
    height = 600,
    usePagenation = false,
    rowKey,
    excelBtn,
    setExcelBtn,
    onClickCheckBox,
    visibleTotalCount,
    scrollX = true,
    visiblePages = 10,
    summary = {},
    rowHeight = 48,
    frozenCount = 0,
    dashboard,
    rowClickCheckBox,
    checkedColumn,
    eListYn = 'N',
    eExcelYn = 'N',
    modal,
    fileName: xlsxName,
    setRowKey,
    customCheck = false,
    draggable = false,
    isDisable,
    isUnChkAll,
    comparisonChkKey,
    comparisonChkList,
    pageNum: pageNumProps,
    gridId,
    idxKey,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(data.length);
  const [pageNum, setPageNum] = useState<any>(pageNumProps || 1);
  const [activeChk, setActiveChk] = useState<any[]>([]);
  const queryParams = new URLSearchParams(location.search);
  const maxPageNum = Math.ceil(data.length / perPage) <= 0 ? 1 : Math.ceil(data.length / perPage);
  const foldMenu = useRecoilValue(asideFoldState);
  const [tuiHeight, setTuiHeight] = useState<any>(height);
  const [isSorted, setIsSorted] = useState(false);
  const [privateColumns, setPrivateColumns] = useState<any[]>([]);

  useEffect(() => {
    const newPrivateColumns = columns.filter((col) => col.isPrivate);
    if (newPrivateColumns.length > 0) {
      setPrivateColumns(newPrivateColumns);
    }
  }, [columns]);

  useEffect(() => {
    const newData =
      eListYn === 'Y'
        ? data.map((row: any) => {
            // 각 row의 마스킹 컬럼만 마스킹 처리
            const maskedRow = { ...row };
            privateColumns.forEach((col: any) => {
              maskedRow[col.name] = maskSecretValue(row[col.name], col.maskingType);
              maskedRow[`origin${col.name}`] = row[col.name];
            });
            return maskedRow;
          })
        : data.map((row: any) => {
            // 각 row의 마스킹 컬럼만 마스킹 처리
            const maskedRow = { ...row };
            privateColumns.forEach((col: any) => {
              maskedRow[col.name] = normalizeMaskingValue(row[col.name], col.maskingType);
              maskedRow[`origin${col.name}`] = row[col.name];
            });
            return maskedRow;
          });
    setDataState(newData);
    if (setFilterTableState) setFilterTableState(newData);
    setTotalCount(data.length);
    setPageNum(1);
  }, [data, perPage, privateColumns]);

  useEffect(() => {
    data.map((v: any, i: number) => {
      v.rowKey = dataState[i]?.rowKey;
    });

    if (!modal) {
      grid?.getPagination()?.movePageTo(Number(pageNum));
    }

    grid?.on('beforeFilter', (ev: any) => {
      if (setRowKey) setRowKey('');
    });

    grid?.on('afterFilter', (ev: any) => {
      dataState.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      if (setFilterTableState) {
        setFilterTableState(ev.instance.store.data.filteredRawData);
      }
    });

    grid?.on('afterUnfilter', (ev: any) => {
      dataState.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      if (setFilterTableState) {
        setFilterTableState(ev.instance.store.data.filteredRawData);
      }
    });

    grid?.on('afterSort', (ev: any) => {
      dataState.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      if (setFilterTableState) {
        setFilterTableState(ev.instance.store.data.filteredRawData);
      }
    });

    grid?.on('afterUnsort', (ev: any) => {
      dataState.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      if (setFilterTableState) {
        setFilterTableState(ev.instance.store.data.filteredRawData);
      }
    });

    grid?.on('drop', (ev: any) => {
      dataState.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      if (setFilterTableState) {
        setFilterTableState(ev.instance.store.data.filteredRawData);
      }
    });
  }, [dataState, grid]);

  // 모든 행 체크 해제
  useEffect(() => {
    if (isUnChkAll) grid?.uncheckAll();
  }, [isUnChkAll]);

  // 값이 있는 행에 Checkd 부여
  useEffect(() => {
    if (activeChk) {
      activeChk.map((item: any) => grid?.check(item.rowKey));
    }
  }, [activeChk]);

  // 그리드 마운트 후 이벤트
  const onGridUpdateFn = (ev: any) => {
    if (comparisonChkKey) {
      const activeChkList =
        ev?.instance?.getData()?.filter((item: any) => {
          return comparisonChkList?.some((item2: any) => item[comparisonChkKey] === item2[comparisonChkKey]);
        }) || [];

      setActiveChk(activeChkList);
    }

    // 그리드 초기화 완료 후 paste 차단 (최초 1회만 등록)
    const rootEl = gridRef.current?.getRootElement();
    if (rootEl && !rootEl.dataset.pasteBlocked) {
      rootEl.dataset.pasteBlocked = 'true';
      rootEl.addEventListener(
        'keydown',
        (e: KeyboardEvent) => {
          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
      rootEl.addEventListener(
        'paste',
        (e: ClipboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );
    }
  };

  /**
   * 페이지네이션 버튼 클릭 시 URL 파라미터 업데이트
   * - 각 그리드별로 고유한 페이지 상태를 유지하기 위해 gridId를 사용
   * - URL 파라미터 형식: grid1_pageNum=1&grid2_pageNum=2
   * - 기존의 단일 pageNum 파라미터를 각 그리드별 파라미터로 분리
   */
  useEffect(() => {
    const beforePageMoveListener = (ev: any) => {
      if (!dashboard && !modal) {
        const currentSearchParams = new URLSearchParams(location.search);
        const currentPageNum = gridId ? currentSearchParams.get(`${gridId}_pageNum`) : currentSearchParams.get('pageNum');

        const newPageNum = ev.page.toString();
        const searchParams = new URLSearchParams(location.search);

        // 기존 URL 파라미터를 유지하면서 pageNum만 추가/업데이트
        if (gridId) {
          searchParams.set(`${gridId}_pageNum`, newPageNum);
        } else {
          searchParams.set('pageNum', newPageNum);
        }
        const newSearch = searchParams.toString();

        // 현재 URL과 비교하여 변경이 있을 때만 네비게이션
        const currentSearch = location.search;
        const currentSearchString = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;
        if (currentSearchString !== newSearch) {
          // URL에 pageNum이 없고 새로 추가되는 pageNum이 1인 경우는 초기 로드로 간주하여 replace 사용
          const isInitialLoad = !currentPageNum && newPageNum === '1';

          navigate(
            {
              pathname: location.pathname,
              search: newSearch,
            },
            isInitialLoad ? { replace: true } : undefined
          );
        }
      }
    };

    if (grid && usePagenation) {
      grid.on('beforePageMove', beforePageMoveListener);
    }

    // 언마운트시 이벤트리스너 해제
    return () => {
      if (grid && Object.keys(grid).length > 0 && usePagenation) {
        grid?.off('beforePageMove', beforePageMoveListener);
      }
    };
  }, [dataState, gridId, location.pathname, location.search, navigate, dashboard, modal]);
  // }, [dataState, gridId]);

  /**
   * URL 파라미터 변경 감지 및 페이지 이동 처리
   * - URL의 gridId_pageNum 파라미터를 감지하여 해당 그리드의 페이지 이동
   * - 각 그리드는 자신의 gridId에 해당하는 파라미터만 처리
   * - 페이지 이동 시 그리드의 상태도 함께 업데이트
   */
  useEffect(() => {
    if (!dashboard && location.search) {
      const searchParams = new URLSearchParams(location.search);
      // 그리드별 파라미터
      const pageNumParam = searchParams.get(`${gridId}_pageNum`);
      // 기존 파라미터
      const pageNumParam2 = searchParams.get('pageNum');

      if (setPageNum) {
        const pNum = gridId ? pageNumParam : pageNumParam2;

        if (pNum !== null && pNum !== undefined && !isNaN(Number(pNum))) {
          const pageNumber = Number(pNum);
          setPageNum(pageNumber);
          grid?.getPagination()?.movePageTo(pageNumber);
        }
      }
    }
  }, [location.search, gridId]);

  /**
   *  페이지 번호 변경 시 그리드 페이지 이동
   * - pageNum 상태가 변경될 때마다 그리드의 페이지를 해당 번호로 이동
   * - 각 그리드는 자신의 pageNum 상태만 처리
   */
  useEffect(() => {
    if (pageNum) {
      grid?.getPagination()?.movePageTo(pageNum);
    }
  }, [pageNum]);

  useEffect(() => {
    if (grid && dataState) {
      try {
        grid.resetData(dataState, { pageState: { page: 1, perPage } });
      } catch (error) {
        console.error('Error resetting data in TUI Grid:', error);
      }
    }
  }, [gridRef, perPage, dataState]);

  useEffect(() => {
    const handleExport = (ev: any) => {
      ev.stop();
      const privateColumnIdxs: number[] = [];

      const newDataList = ev.data.map((v: any, i: number) => {
        let dt: any;
        if (filterTableState) dt = filterTableState;
        else dt = data;

        if (i === 0) {
          privateColumns.forEach((col: any) => {
            privateColumnIdxs.push(v.findIndex((v2: any) => v2 === col.header));
          });
          // wNameIdx = v.findIndex((v2: any) => v2 === t('근로자명'));
        } else {
          const newData = v;
          newData[0] = newData[0] != null ? String(newData[0]).replace('No.', '') : newData[0];
          /**
           * 수정자 : 한영광
           * 수정일자 : 2023.11.09
           * 수정내용 : 암호화 할 근로자명을 포함하지 않는 데이터인 경우 엑셀 내보내기 시 빈 데이터가 나오는 버그가 발생하여
           *           eExcelYn이 null이 아닌것을 암호화 할 근로자명이 있는것으로 예외처리 함
           *           **근로자명 컬럼의 순서를 통일해줘야 하고 순서가 바뀌면 아래 인덱스값도 수정해야함
           *              -> 만약 순서를 통일 할 수 없다면 파라미터로 컬럼순서를 받아서 처리 할 예정
           */
          privateColumnIdxs.forEach((col: any, idx: number) => {
            if (eListYn === 'N' && eExcelYn === 'Y') {
              newData[col] = maskSecretValue(newData[col], privateColumns[idx].maskingType);
            } else if (eListYn === 'Y' && eExcelYn === 'N') {
              newData[col] = normalizeMaskingValue(dt[i - 1]?.[`origin${privateColumns[idx].name}`], privateColumns[idx].maskingType);
            }
          });
          if (ev.data[0].some((v2: string) => v2 === t('주소'))) {
            const addressIndex = ev.data[0].findIndex((v2: string) => v2 === t('주소'));
            const dataSource = filterTableState || data;
            newData[addressIndex] = dataSource[i - 1]?.address || '';
          }
          if (ev.data[0].some((v2: string) => v2 === t('공유항목'))) {
            newData[ev.data[0].findIndex((v2: string) => v2 === t('공유항목'))] = data[i - 1].csName;
          }
          return [...newData];
        }
        return [...v];
      });

      // excludeFromExport: true인 컬럼들을 찾아서 제거
      const excludeColumnIndices: number[] = [];
      columns.forEach((col: any) => {
        if (col.excludeFromExport === true && col.header) {
          const index = ev.data[0]?.findIndex((v2: any) => v2 === col.header);
          if (index !== -1) {
            excludeColumnIndices.push(index);
          }
        }
      });

      // 내림차순으로 정렬하여 뒤에서부터 제거 (인덱스 변경 방지)
      excludeColumnIndices.sort((a, b) => b - a);

      // 각 하위 배열에서 excludeFromExport가 true인 컬럼 제거
      if (excludeColumnIndices.length > 0) {
        for (const subArray of newDataList) {
          excludeColumnIndices.forEach((index) => {
            subArray.splice(index, 1);
          });
        }
      }

      // 사진컬럼이 있을 때 엑셀보내기 시 사진컬럼 삭제
      const imgIndex = ev.data[0].indexOf(t('사진'));
      // 첫 번째 하위 배열에 '사진'이 있는 경우
      if (imgIndex !== -1) {
        // 각 하위 배열에서 imgIndex 위치의 값 제거
        for (const subArray of newDataList) {
          subArray.splice(imgIndex, 1);
        }
      }

      // filter: 'number' 컬럼에 문자열로 들어온 숫자값을 실제 숫자 타입으로 변환 (엑셀에서 텍스트로 인식되는 문제 방지)
      const numberColumnIndices: number[] = [];
      columns.forEach((col: any) => {
        if (col.filter === 'number' && col.header) {
          const index = newDataList[0]?.findIndex((v2: any) => v2 === col.header);
          if (index !== -1) numberColumnIndices.push(index);
        }
      });
      if (numberColumnIndices.length > 0) {
        for (let i = 1; i < newDataList.length; i += 1) {
          numberColumnIndices.forEach((colIdx) => {
            const cell = newDataList[i][colIdx];
            if (typeof cell === 'string') {
              const trimmed = cell.trim();
              const num = Number(trimmed);
              if (trimmed !== '' && !isNaN(num) && isFinite(num)) {
                newDataList[i][colIdx] = num;
              }
            }
          });
        }
      }

      ev.exportFn(newDataList);
    };

    if (excelBtn && setExcelBtn) {
      grid?.on('beforeExport', handleExport);
      grid?.export('xlsx', {
        fileName: xlsxName || 'PMS', // 내보낼 파일의 이름을 정의
        onlyFiltered: true,
        includeHiddenColumns: true,
      });
      setExcelBtn(false);
    }

    return () => {
      if (excelBtn && setExcelBtn) {
        grid?.off('beforeExport', handleExport);
      }
    };
  }, [excelBtn, filterTableState]);

  useEffect(() => {
    if (grid && onClickCheckBox) {
      grid?.on('checkAll', () => {
        onClickCheckBox(grid?.getCheckedRows());
      });
      grid?.on('uncheckAll', () => {
        onClickCheckBox(grid?.getCheckedRows());
      });
      grid?.on('check', () => {
        onClickCheckBox(grid?.getCheckedRows());
      });
      grid?.on('uncheck', () => {
        onClickCheckBox(grid?.getCheckedRows());
      });
    }
  }, [onClickCheckBox, grid]);

  useEffect(() => {
    dataState.map((_: any, i: number) => {
      grid?.removeRowClassName(i, 'selectedRow');
    });

    if (rowKey !== undefined && rowKey !== '' && rowKey !== null && dataState.length > Number(rowKey)) {
      grid?.addRowClassName(Number(rowKey), 'selectedRow');
      if (!isSorted) grid?.focusAt(Number(rowKey), 0, true);
    }
  }, [rowKey, dataState]);

  useEffect(() => {
    if (rowKey !== undefined && rowKey !== '' && filterTableState && Number(rowKey) < dataState.length) {
      grid?.focusAt(Number(rowKey), 0, true);

      data.map((_: any, i: number) => {
        grid?.removeRowClassName(i, 'selectedRow');
      });
      grid?.addRowClassName(filterTableState[Number(rowKey)]?.rowKey, 'selectedRow');
    }
    if (filterTableState) {
      setTotalCount(filterTableState.length);
    }

    grid?.on('afterPageMove', () => setPageNum(1)); // tui 테이블이 2개일 경우 왼쪽 테이블 행이 변경 시 페이징 초기화
  }, [rowKey, filterTableState]);

  useEffect(() => {
    grid?.getPagination()?.movePageTo(Number(pageNum));
    dataState.forEach((row) => {
      if (row.useYn === 'N' && isDisable) {
        grid?.disableRow(row.rowKey, true);
      }
    });
  }, [pageNum, dataState]);

  useEffect(() => {
    /**
     * 수정자 : 홍선영
     * 수정일자 : 2024.05.14
     * 수정내용 : 근로자 서명관리에서 tuigrid 외부의 상단 컬럼전체 체크박스 클릭시 모든 로우의 컬럼값 업데이트 처리 추가
     */
    if (checkedColumn?.columnName) {
      grid?.setColumnValues(checkedColumn.columnName, checkedColumn.checked);

      if (grid?.getData()) setDataState(grid?.getData());
      if (onClickCheckBox) onClickCheckBox(grid?.getCheckedRows());
    }
  }, [checkedColumn, gridRef]);

  type SortInfo = {
    [columnName: string]: boolean;
  };
  const [sortInfo, setSortInfo] = useState<SortInfo>({});

  const onClick = (ev: any) => {
    if (ev.targetType === 'columnHeader') {
      grid?.sort(ev.columnName, sortInfo[ev.columnName] ? !sortInfo[ev.columnName] : true);
      setSortInfo((prev) => ({ ...prev, [ev.columnName]: !prev[ev.columnName] }));
      setIsSorted(true);
    }
    if (onClickRow && !clickDisable) {
      if (
        ev.rowKey !== undefined &&
        ev.columnName !== '_checked' &&
        ev.columnName !== 'wWorkstatus' &&
        ev.columnName !== 'aImgPath' &&
        ev.columnName !== 'button' &&
        ev.columnName !== 'workerInfoCheck' &&
        ev.columnName !== 'cCheck' && // 공통 양식지
        ev.columnName !== 'jCheck' && // 직종별 양식지
        ev.columnName !== 'muscularCheck' &&
        ev.columnName !== 'painCheck' &&
        ev.columnName !== 'stressCheck' &&
        ev.targetType !== 'columnHeader'
      ) {
        let filterRowKey: any;
        if (filterTableState) {
          filterRowKey = filterTableState?.findIndex((v) => v.rowKey === ev.rowKey);
        } else {
          filterRowKey = ev.rowKey;
        }
        onClickRow(dataState[ev.rowKey] ?? dataState[filterRowKey], ev.columnName, filterRowKey);

        data.map((_: any, i: number) => {
          grid?.removeRowClassName(i, 'selectedRow');
        });
        grid?.addRowClassName(Number(filterRowKey), 'selectedRow');
        if (rowClickCheckBox && onClickCheckBox) {
          grid?.check(filterRowKey);
          onClickCheckBox(grid?.getCheckedRows());
        }
      } else if (summaryCheck() && ev.targetType === 'dummy') {
        /**
         * 수정자 : 한영광
         * 수정일자 : 2023.11.20
         * 수정내용 : 연출역 현황에서 summary(합계 나타내는 로우)를 사용하는데 summary 클릭 시 이벤트 처리를 위해 추가함
         */
        onClickRow('summary', ev.nativeEvent.srcElement.getAttribute('data-column-name'));
      }
    }

    /**
     * 수정자 : 홍선영
     * 수정일자 : 2024.05.14
     * 수정내용 : 근로자 서명관리에서 로우 내부의 checkbox 클릭시 해당셀 값 업데이트 처리 추가
     */
    if (customCheck) {
      if (
        ev.rowKey !== undefined &&
        ev.columnName !== 'wName' &&
        ev.columnName !== 'sjName' &&
        ev.columnName !== 'wPrejobtypeName' &&
        ev.columnName !== 'wJobtypeName' &&
        ev.columnName !== 'wJobdate' &&
        ev.columnName !== 'wWorkstatusName' &&
        ev.columnName !== 'wHnum' &&
        ev.columnName !== 'stressTotal' &&
        ev.columnName !== 'stressResult' &&
        ev.columnName !== 'wBdate' &&
        ev.columnName !== 'wRetiredate' &&
        ev.targetType !== 'columnHeader' &&
        ev.nativeEvent.target.type === 'checkbox'
      ) {
        // let filterRowKey: any;
        // if (filterTableState) {
        //   filterRowKey = filterTableState?.findIndex((v) => v.rowKey === ev.rowKey);
        // } else {
        //   filterRowKey = ev.rowKey;
        // }

        grid?.setValue(ev.rowKey, ev.columnName, !grid?.getValue(ev.rowKey, ev.columnName));

        // grid?.check(filterRowKey);
        if (onClickCheckBox) onClickCheckBox(grid?.getCheckedRows());
      }
    }
  };
  const summaryCheck = () => {
    if (Object.keys(summary).length !== 0 && data.length !== 0) return true;
    return false;
  };
  useEffect(() => {
    // pagenation이 있을때
    if (usePagenation === true) {
      setTuiHeight(height - 84);
    }
    // pagenation이 없고 summary가 있고 데이터가 없을때
    if (usePagenation === false && Object.keys(summary).length !== 0 && dataState.length === 0) {
      setTuiHeight(height - 12);
    }
    // pagenation이 없고 summary가 있고 데이터가 있을때
    if (usePagenation === false && Object.keys(summary).length !== 0 && dataState.length !== 0) {
      setTuiHeight(height - 80);
    }
    // pagenation이 없고 summary가 없을때
    if (usePagenation === false && Object.keys(summary).length === 0) {
      setTuiHeight(height - 40);
    }
  }, [height, dataState.length]);

  // side menu가 fold되면 리렌더링
  useLayoutEffect(() => {
    if (grid) grid?.refreshLayout();
  }, [foldMenu, gridRef]);

  /**
   * 수정자 : 한영광
   * 수정일자 : 2024.01.29
   * 수정내용 : Summary 데이터가 변경된 경우 TuiGrid 라이브러리에 변경된 데이터가 반영이 안되는 이슈 발생
   *           Summary 데이터 변경 시 TuiGrid 내부함수 setSummaryColumnContent()를 사용하여 변경된 Summary 데이터를 반영하도록 수정
   */
  useEffect(() => {
    if (summaryCheck()) {
      Object.keys(summary.columnContent).map((v: string) => {
        grid?.setSummaryColumnContent(v, summary.columnContent[v]);
      });
    }
  }, [summary]);

  const handleResize = () => {
    if (grid) {
      grid?.refreshLayout();
    }
  };

  return (
    <TuiGridStyle>
      {!summaryCheck() ? ( // summary 사용하지 않는경우
        <>
          <Grid
            key={key}
            draggable={draggable}
            data={dataState}
            columns={columns}
            rowHeaders={
              onClickCheckBox
                ? [
                    {
                      type: 'checkbox',
                      header: `
                          <label for="all-checkbox" class="checkbox">
                            <input type="checkbox" id="all-checkbox" class="hidden-input" name="_checked" />
                            <span class="custom-input"></span>
                          </label>
                        `,
                    },
                    { type: 'rowNum', width: 40, align: 'center', renderer: { classNames: ['tui-num'] } },
                  ]
                : idxKey
                ? idxKey === 'not use'
                  ? []
                  : [{ type: 'rowNum', width: 40, align: 'center', renderer: { classNames: ['tui-num'] }, name: idxKey, header: 'No.', sortable: true }]
                : [{ type: 'rowNum', width: 40, align: 'center', renderer: { classNames: ['tui-num'] } }]
            } // 맨 앞에 No. 생성 (버그있는 듯)
            bodyHeight={tuiHeight}
            rowHeight={rowHeight !== undefined ? rowHeight : 56}
            usageStatistics={false}
            columnOptions={{ resizable: true, frozenCount, frozenBorderWidth: 0 }}
            pageOptions={
              usePagenation
                ? {
                    useClient: true, // 프론트엔드에서 페이징 사용할지 여부
                    perPage, // 페이징할 row 갯수
                    // page: Number(pageNum) ? (Number(pageNum) > maxPageNum ? maxPageNum : Number(pageNum)) : 1, // 디폴트 페이지번호
                    page: pageNum,
                    visiblePages,
                  }
                : undefined
            }
            scrollX={scrollX}
            scrollY
            width='auto'
            onClick={(ev: any) => onClick(ev)}
            ref={gridRef}
            onGridUpdated={(ev: any) => onGridUpdateFn(ev)}
            // oneTimeBindingProps={['data']}
          />
          {visibleTotalCount && (
            <h5 className='gridCount'>
              {t('총')} {totalCount}
              {t('개가 검색되었습니다')}.
            </h5>
          )}
        </>
      ) : (
        // summary 사용 하는경우
        <>
          <div style={{ display: 'none' }}>{`${summary}`}</div>
          <Grid
            key={key}
            draggable={draggable}
            data={dataState}
            columns={columns}
            summary={summary}
            rowHeaders={
              onClickCheckBox
                ? [
                    {
                      type: 'checkbox',
                      header: `
                        <label for="all-checkbox" class="checkbox">
                          <input type="checkbox" id="all-checkbox" class="hidden-input" name="_checked" />
                          <span class="custom-input"></span>
                        </label>
                      `,
                    },
                    { type: 'rowNum', width: 40, align: 'center', renderer: { classNames: ['tui-num'] } },
                  ]
                : [{ type: 'rowNum', width: 40, align: 'center', renderer: { classNames: ['tui-num'] } }]
            }
            // bodyHeight={height - 90}
            bodyHeight={tuiHeight}
            rowHeight={rowHeight !== undefined ? rowHeight : 56}
            usageStatistics={false}
            columnOptions={{ resizable: true, frozenCount, frozenBorderWidth: 0 }}
            pageOptions={
              usePagenation
                ? {
                    useClient: true, // 프론트엔드에서 페이징 사용할지 여부
                    perPage, // 페이징할 row 갯수
                    // page: Number(pageNum) ? (Number(pageNum) > maxPageNum ? maxPageNum : Number(pageNum)) : 1, // 디폴트 페이지번호
                    page: pageNum,
                    visiblePages,
                  }
                : undefined
            }
            scrollX
            scrollY
            onClick={(ev: any) => onClick(ev)}
            ref={gridRef}
            width='auto'
            // oneTimeBindingProps={['data']}
          />
          {visibleTotalCount && (
            <h5 className='gridCount'>
              {t('총')} {totalCount}
              {t('개가 검색되었습니다')}.
            </h5>
          )}
        </>
      )}
    </TuiGridStyle>
  );
};

export default TuiGrid;
